// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

// Starter Custom Code
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { resolveColor } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import constants from "@store/constants";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

/* Store Custom Code */
import useParentProductState from "@store/Framework/Store/Foundation/Data/Products/ProductsHooks/useParentProductState";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsAttributesStyles {
    wrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;

    // Starter Custom Code
    list?: TypographyPresentationProps;
    item?: TypographyPresentationProps;
    noResult?: TypographyPresentationProps;
}

export const attributesStyles: ProductDetailsAttributesStyles = {
    wrapper: {},
    attributeTypeLabelText: {
        css: css`
            width: calc(50% - 0.5em);
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
        `,
    },
    attributeTypeValuesText: {
        css: css`
            width: calc(50% - 0.5em);
        `,
    },
    list: {
        css: css`
            display: flex;
            flex-flow: row wrap;
            justify-content: space-between;
            word-wrap: break-word;
            /* Bunzl Custom Code */
            ${breakpointValues([0, null, null, "5%"], "padding-inline")}

            font-weight: ${({ theme }) => theme.design.typography.size.smaller};
        `,
    },
    item: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            /* Bunzl Custom Code */
            padding-block: ${pxToRem(14)};
            ${breakpointValues([0, null, null, "1.25em"], "padding-inline")}
            background-color: ${getColor("common.background")};
            /* Bunzl Custom Code */
            border-bottom: 1px solid ${getColor("common.borderLight")};

            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: calc(50% - ${pxToRem(15)});
                            /* bunzl removed code nth child */
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    noResult: {
        forwardAs: "p",
    },
};

const styles = attributesStyles;

const ProductDetailsAttributes: React.FC<Props> = ({ product: { brand, attributeTypes } }) => {
    /* Store Custom Code - Start */
    const { attributeTypes: parentAttributeTypes } = useParentProductState();
    const combinedParentAndChildAttributes = [...(attributeTypes || []), ...(parentAttributeTypes || [])].filter(
        (attribute, index, self) =>
            attribute.name !== constants.attributeTypes.BCHVendorSku &&
            self.findIndex(a => a.id === attribute.id) === index,
    );
    if (!brand && (!combinedParentAndChildAttributes || combinedParentAndChildAttributes.length === 0)) {
        /* Starter Custom Code */
        return (
            <Typography
                {...styles.noResult}
                data-test-selector="productDetails_attributes_noResult" /* Store Custom Code */
            >
                {translate("There are no specifications available.")}
            </Typography>
        );
        /* Starter Custom Code */
    }
    /* Store Custom Code - End */

    // Starter Removed Code - limitedAttributeTypes

    return (
        /* Starter Custom Code */
        <Typography
            as="ul"
            {...styles.list}
            data-test-selector="productDetails_attributes_list" /* Store Custom Code */
        >
            {brand && (
                <Typography as="li" data-test-selector="productDetails_attributes_brandItem" {...styles.item}>
                    <Typography
                        data-test-selector="productDetails_attributes_brandLabel" /* Store Custom Code */
                        {...styles.attributeTypeLabelText}
                    >
                        {translate("Brand")}
                    </Typography>
                    <Typography
                        data-test-selector="productDetails_attributes_brandValue" /* Store Custom Code */
                        {...styles.attributeTypeValuesText}
                    >
                        {brand.name}
                    </Typography>
                </Typography>
            )}
            {/* Store Custom Code - Start */}
            {combinedParentAndChildAttributes &&
                combinedParentAndChildAttributes.map(attributeType => (
                    <Typography
                        as="li"
                        key={attributeType.id.toString()}
                        data-test-selector={`productDetails_attributes_attributeItem_${attributeType.id}`} /* Store Custom Code */
                        data-attributetypeid={attributeType.id}
                        {...styles.item}
                    >
                        <Typography
                            data-test-selector={`productDetails_attributes_attributeItemLabel_${attributeType.id}`} /* Store Custom Code */
                            {...styles.attributeTypeLabelText}
                        >
                            {attributeType.label || attributeType.name}
                        </Typography>
                        <Typography
                            data-test-selector={`productDetails_attributes_attributeItemValue_${attributeType.id}`} /* Store Custom Code */
                            {...styles.attributeTypeValuesText}
                        >
                            {(attributeType.attributeValues ?? [])
                                .map(attributeValue => attributeValue.valueDisplay)
                                .join(", ")}
                        </Typography>
                    </Typography>
                ))}
            {/* Store Custom Code - End */}
        </Typography>
    );
    /* Starter Custom Code */
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsAttributes),
    definition: {
        displayName: "Attributes",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
